import { useEffect, useMemo } from 'react';
import { VideoJsPlayer } from 'video.js';

import { useLogger } from '../../../contexts/logger-context';
import { PlayerOptions } from '../player.interface';
import { VideoJsNativeEvent } from '../videojs-event';

const SKIP_TIME = 10;
const MEDIA_SESSION_EXISTS = Boolean(navigator.mediaSession);

export function useMediaSession(player: VideoJsPlayer, playerOptions: PlayerOptions) {
  const { logger: mainLogger } = useLogger();
  const logger = useMemo(() => mainLogger.getSubLogger({ name: 'useMediaSession' }), [mainLogger]);

  useEffect(() => {
    if (!MEDIA_SESSION_EXISTS) {
      logger.info('Media Session API not supported');
      return;
    }
    logger.info('Media Session API supported');

    const onLoadStart = () => {
      logger.info('Load start - updating media session metadata');
      updateMediaSession(playerOptions);
    };

    const updateState = () => {
      logger.info(`Playback ${player.paused() ? 'paused' : 'playing'} - updating playback state`);
      navigator.mediaSession.playbackState = player.paused() ? 'paused' : 'playing';
    };

    const onEnded = () => {
      logger.info('Playback ended - setting playback state to none');
      navigator.mediaSession.playbackState = 'none';
    };

    logger.info('Setting up media session action handlers');

    setUpActionHandler(player);

    player.on(VideoJsNativeEvent.LOAD_START, onLoadStart);
    player.on(VideoJsNativeEvent.PLAY, updateState);
    player.on(VideoJsNativeEvent.PAUSE, updateState);
    player.on(VideoJsNativeEvent.ENDED, onEnded);

    logger.info('Setting initial media session metadata');
    updateMediaSession(playerOptions);

    return () => {
      logger.info('Clearing media session metadata');

      navigator.mediaSession.metadata = null;
      navigator.mediaSession.setActionHandler('play', null);
      navigator.mediaSession.setActionHandler('pause', null);
      navigator.mediaSession.setActionHandler('seekto', null);
      navigator.mediaSession.setActionHandler('seekbackward', null);
      navigator.mediaSession.setActionHandler('seekforward', null);
      navigator.mediaSession.setActionHandler('stop', null);

      player.off(VideoJsNativeEvent.LOAD_START, onLoadStart);
      player.off(VideoJsNativeEvent.PLAY, updateState);
      player.off(VideoJsNativeEvent.PAUSE, updateState);
      player.off(VideoJsNativeEvent.ENDED, onEnded);
    };
  }, [logger, player, playerOptions]);
}

function setUpActionHandler(player: VideoJsPlayer) {
  navigator.mediaSession.setActionHandler('play', () => player.play());
  navigator.mediaSession.setActionHandler('pause', () => player.pause());
  navigator.mediaSession.setActionHandler('seekto', (event) => {
    if (event.seekTime !== undefined) {
      setCurrentTime(player, event.seekTime);
    }
  });
  navigator.mediaSession.setActionHandler('seekbackward', (event) => {
    const skip = event.seekOffset || SKIP_TIME;
    setCurrentTime(player, Math.max(player.currentTime() - skip, 0));
  });
  navigator.mediaSession.setActionHandler('seekforward', (event) => {
    const skip = event.seekOffset || SKIP_TIME;
    setCurrentTime(player, Math.min(player.currentTime() + skip, player.duration()));
  });
  navigator.mediaSession.setActionHandler('stop', () => {
    player.pause();
    setCurrentTime(player, 0);
    navigator.mediaSession.playbackState = 'none';
  });
}

function setCurrentTime(player: VideoJsPlayer, time: number) {
  player.currentTime(time);
  updatePositionState(player);
}

function updatePositionState(player: VideoJsPlayer) {
  navigator.mediaSession.setPositionState({
    duration: player.duration() || 0,
    playbackRate: player.playbackRate(),
    position: player.currentTime(),
  });
}

function updateMediaSession(playerOptions: PlayerOptions) {
  const { title, description } = playerOptions.metadata;
  const { poster } = playerOptions;

  let artwork: MediaImage[] | undefined;
  if (poster?.src) {
    if (poster.set.length > 0) {
      artwork = poster.set.map((set) => ({
        src: set.src,
        sizes: `${set.width}x${set.height}`,
        type: set.mimeType,
      }));
    } else {
      artwork = [{ src: poster.src }];
    }
  }

  const srcMetadata: MediaMetadataInit = {
    title: title || 'Unknown title',
    album: description || 'Unknown album',
    artwork,
  };

  navigator.mediaSession.metadata = new MediaMetadata(srcMetadata);
}
